/**
 * A volume knob's arithmetic (Dax, 2026-09-29): one control for a level and its mute. Dragging turns it
 * (right or up is clockwise), turning past the bottom of the range mutes, turning back up unmutes at the
 * bottom. Pure, so the feel is tested.
 */

/** How far a drag moves for one dB. */
export const KNOB_PX_PER_DB = 4;
/** How far past the bottom of the range a drag must go before it mutes. */
export const KNOB_MUTE_DB = 2;
/** A press that moves less than this is a click (type a value), not a turn. */
export const KNOB_CLICK_SLOP_PX = 3;
/** A second click within this long is a double-click (mute), so a single click waits this long before typing opens. */
export const KNOB_DOUBLE_CLICK_MS = 250;
/** The dial's sweep, in degrees clockwise from straight up: mute at the far left, the loudest at the far right. */
export const KNOB_SWEEP_FROM_DEG = -135;
export const KNOB_SWEEP_TO_DEG = 135;
/** The gap on the dial between mute and the bottom of the range. */
export const KNOB_MUTE_GAP_DEG = 20;

export interface KnobValue { db: number; muted: boolean }
export interface KnobRange { min: number; max: number }

/** Whether a pointer event's `buttons` still hold the main button: a move without it is a release that went missing. */
export function knobHeld(buttons: number): boolean {
  return (buttons & 1) === 1;
}

/** Where a drag that began at `from` has turned the knob to, `dx` right and `dy` down in pixels. */
export function knobTurn(from: KnobValue, dx: number, dy: number, r: KnobRange): KnobValue {
  const start = from.muted ? r.min - KNOB_MUTE_DB : from.db;
  const raw = start + (dx - dy) / KNOB_PX_PER_DB;
  if (raw <= r.min - KNOB_MUTE_DB) return { db: from.muted ? from.db : r.min, muted: true };
  return { db: Math.round(Math.max(r.min, Math.min(r.max, raw))), muted: false };
}

/** A typed value: a number within range, or anything below it mutes. Null when it is not a number. */
export function knobTyped(text: string, r: KnobRange): KnobValue | null {
  const n = Number(text.trim().replace(/db$/i, '').trim());
  if (!text.trim() || !Number.isFinite(n)) return null;
  if (n < r.min) return { db: r.min, muted: true };
  return { db: Math.round(Math.min(r.max, n)), muted: false };
}

/** The pointer's angle on the dial for a value. */
export function knobAngle(v: KnobValue, r: KnobRange): number {
  if (v.muted) return KNOB_SWEEP_FROM_DEG;
  const lo = KNOB_SWEEP_FROM_DEG + KNOB_MUTE_GAP_DEG;
  return lo + ((v.db - r.min) / (r.max - r.min)) * (KNOB_SWEEP_TO_DEG - lo);
}

/** An SVG arc along the dial from one angle to another, on a circle of radius `rad` centred at `c`. */
export function arcPath(c: number, rad: number, fromDeg: number, toDeg: number): string {
  const pt = (deg: number) => {
    const a = ((deg - 90) * Math.PI) / 180;
    return `${(c + rad * Math.cos(a)).toFixed(2)} ${(c + rad * Math.sin(a)).toFixed(2)}`;
  };
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${pt(fromDeg)} A ${rad} ${rad} 0 ${large} 1 ${pt(toDeg)}`;
}
